/**
 * Système d'animations modulaire pour Shopify + Tailwind
 * Compatible avec Liquid et optimisé pour les performances
 */

class AnimationManager {
  constructor() {
    this.animations = new Map();
    this.observers = new Map();
    this.parallaxItems = new Map();
    this.parallaxTicking = false;
    this.parallaxRafId = null;
    this.parallaxListenersAttached = false;
    this.handleParallaxScroll = this.handleParallaxScroll.bind(this);
    this.init();
  }

  init() {
    // Attendre que le DOM soit prêt
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupObservers());
    } else {
      this.setupObservers();
    }
  }

  setupObservers() {
    // Observer pour les animations d'entrée
    this.setupEntryAnimations();
    
    // Observer pour les parallaxes
    this.setupParallaxAnimations();
    
    // Observer pour les animations Lottie
    this.setupLottieAnimations();
    
    // Observer pour les compteurs animés
    this.setupCounterAnimations();
  }

  /**
   * Animations d'entrée d'éléments au scroll
   */
  setupEntryAnimations() {
    const entryObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const animationType = element.dataset.animate || 'fadeInUp';
          const delay = element.dataset.delay || 0;
          
          setTimeout(() => {
            this.triggerEntryAnimation(element, animationType);
          }, parseInt(delay));
          
          entryObserver.unobserve(element);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observer tous les éléments avec data-animate
    document.querySelectorAll('[data-animate]').forEach(el => {
      const animationType = el.dataset.animate || 'fadeInUp';
      this.applyEntryAnimationBase(el, animationType);
      entryObserver.observe(el);
    });

    this.observers.set('entry', entryObserver);
  }

  /**
   * Animations de parallaxe
   */
  setupParallaxAnimations() {
    if (!this.parallaxListenersAttached) {
      window.addEventListener('scroll', this.handleParallaxScroll, { passive: true });
      window.addEventListener('resize', this.handleParallaxScroll);
      this.parallaxListenersAttached = true;
    }

    const parallaxObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const element = entry.target;

        if (entry.isIntersecting) {
          this.registerParallaxItem(element);
          this.handleParallaxScroll();
        } else {
          this.unregisterParallaxItem(element);
        }
      });
    }, {
      threshold: 0
    });

    // Observer tous les éléments avec data-parallax
    document.querySelectorAll('[data-parallax]').forEach(el => {
      parallaxObserver.observe(el);
    });

    this.observers.set('parallax', parallaxObserver);
  }

  /**
   * Animations Lottie
   */
  setupLottieAnimations() {
    // Charger toutes les animations Lottie avec la première frame
    document.querySelectorAll('[data-lottie-id]').forEach(el => {
      this.loadLottieFirstFrame(el);
    });

    const lottieObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const animationId = element.dataset.lottieId;
          
          // Démarrer l'animation si elle n'a pas encore été jouée
          if (animationId && this.animations.has(animationId) && !element.dataset.lottiePlayed) {
            this.playLottieAnimation(element, animationId);
            // Marquer comme jouée pour éviter les re-déclenchements
            element.dataset.lottiePlayed = 'true';
            // Ne plus observer cet élément
            lottieObserver.unobserve(element);
          }
        }
      });
    }, {
      threshold: 0.3
    });

    // Observer tous les éléments avec data-lottie-id
    document.querySelectorAll('[data-lottie-id]').forEach(el => {
      lottieObserver.observe(el);
    });

    this.observers.set('lottie', lottieObserver);
  }

  /**
   * Charger la première frame de l'animation Lottie
   */
  async loadLottieFirstFrame(element) {
    // Charger Lottie si nécessaire
    if (typeof lottie === 'undefined') {
      await this.loadLottieLibrary();
    }

    const animationId = element.dataset.lottieId;
    const animationPath = element.dataset.lottiePath;
    const loop = element.dataset.lottieLoop === 'true';
    const speed = parseFloat(element.dataset.lottieSpeed) || 1;

    try {
      const animation = lottie.loadAnimation({
        container: element,
        renderer: 'svg',
        loop: loop,
        autoplay: false, // Ne pas démarrer automatiquement
        path: animationPath
      });

      // Appliquer la vitesse personnalisée
      animation.setSpeed(speed);

      // Aller à la première frame et s'arrêter
      animation.goToAndStop(0, true);

      // Stocker l'animation pour la démarrer plus tard
      this.animations.set(animationId, animation);

      // Ajuster les dimensions du conteneur
      animation.addEventListener('DOMLoaded', () => {
        const svgElement = element.querySelector('svg');
        if (svgElement) {
          const rect = svgElement.getBoundingClientRect();
          if (rect.height > 0) {
            element.style.height = rect.height + 'px';
          }
        }
      });

    } catch (error) {
      console.error('Erreur lors du chargement de l\'animation Lottie:', error);
    }
  }

  /**
   * Démarrer l'animation Lottie
   */
  playLottieAnimation(element, animationId) {
    const animation = this.animations.get(animationId);
    if (animation) {
      animation.play();
    }
  }

  /**
   * Déclencher une animation d'entrée
   */
  triggerEntryAnimation(element, type) {
    // Mapper les types vers les classes CSS
    const animationClass = this.getEntryAnimationClass(type);

    if (animationClass) {
      // Ajouter la classe d'animation initiale si elle n'existe pas
      if (!element.classList.contains(animationClass)) {
        element.classList.add(animationClass);
      }
      
      // Déclencher l'animation en ajoutant la classe active
      element.classList.add('animate-active');
    }
  }

  applyEntryAnimationBase(element, type) {
    const animationClass = this.getEntryAnimationClass(type);

    if (animationClass && !element.classList.contains(animationClass)) {
      element.classList.add(animationClass);
    }
  }

  getEntryAnimationClass(type) {
    const animationMap = {
      'fadeInUp': 'animate-fade-in-up',
      'fadeInDown': 'animate-fade-in-down'
    };

    return animationMap[type] || null;
  }

  /**
   * Déclencher une animation de parallaxe
   */
  triggerParallaxAnimation(element, speed, direction) {
    this.registerParallaxItem(element, { speed, direction });
    this.handleParallaxScroll();
  }

  registerParallaxItem(element, options = {}) {
    const speed = typeof options.speed === 'number'
      ? options.speed
      : parseFloat(element.dataset.parallaxSpeed) || 0.5;
    const direction = options.direction || element.dataset.parallaxDirection || 'up';

    this.parallaxItems.set(element, { speed, direction });
  }

  unregisterParallaxItem(element) {
    if (this.parallaxItems.has(element)) {
      this.parallaxItems.delete(element);
      element.style.transform = '';
    }
  }

  handleParallaxScroll() {
    if (this.parallaxItems.size === 0) {
      return;
    }

    if (!this.parallaxTicking) {
      this.parallaxTicking = true;
      this.parallaxRafId = requestAnimationFrame(() => {
        this.updateParallaxItems();
      });
    }
  }

  updateParallaxItems() {
    const scrolled = window.pageYOffset || window.scrollY || 0;

    for (const [element, config] of this.parallaxItems) {
      if (!document.body.contains(element)) {
        this.parallaxItems.delete(element);
        continue;
      }

      const { speed, direction } = config;
      const rate = scrolled * -speed;

      switch (direction) {
        case 'up':
          element.style.transform = `translateY(${rate}px)`;
          break;
        case 'down':
          element.style.transform = `translateY(${-rate}px)`;
          break;
        case 'left':
          element.style.transform = `translateX(${rate}px)`;
          break;
        case 'right':
          element.style.transform = `translateX(${-rate}px)`;
          break;
        default:
          element.style.transform = `translateY(${rate}px)`;
      }
    }

    this.parallaxTicking = false;
    this.parallaxRafId = null;
  }


  /**
   * Charger la bibliothèque Lottie
   */
  loadLottieLibrary() {
    return new Promise((resolve, reject) => {
      if (typeof lottie !== 'undefined') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Animations de compteur de chiffres
   */
  setupCounterAnimations() {
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const targetValue = parseFloat(element.dataset.counterTarget) || 0;
          const duration = parseInt(element.dataset.counterDuration) || 2000;
          const delay = parseInt(element.dataset.counterDelay) || 0;
          const prefix = element.dataset.counterPrefix || '';
          const suffix = element.dataset.counterSuffix || '';
          const separator = element.dataset.counterSeparator || '';
          
          setTimeout(() => {
            this.triggerCounterAnimation(element, targetValue, duration, prefix, suffix, separator);
          }, delay);
          
          counterObserver.unobserve(element);
        }
      });
    }, {
      threshold: 0.5,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observer tous les éléments avec data-counter-target
    document.querySelectorAll('[data-counter-target]').forEach(el => {
      counterObserver.observe(el);
    });

    this.observers.set('counter', counterObserver);
  }

  /**
   * Déclencher une animation de compteur
   */
  triggerCounterAnimation(element, targetValue, duration, prefix = '', suffix = '', separator = '') {
    const startValue = 0;
    const startTime = performance.now();
    
    // Déterminer le nombre de décimales à afficher
    const decimalPlaces = this.getDecimalPlaces(targetValue);
    
    const animateCounter = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Fonction d'easing pour une animation fluide
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (targetValue - startValue) * easeOutCubic;
      
      // Arrondir selon le nombre de décimales de la valeur cible
      const roundedValue = this.roundToDecimalPlaces(currentValue, decimalPlaces);
      
      // Formater le nombre avec séparateur
      const formattedValue = this.formatNumber(roundedValue, separator, decimalPlaces);
      element.textContent = prefix + formattedValue + suffix;
      
      if (progress < 1) {
        requestAnimationFrame(animateCounter);
      } else {
        // S'assurer que la valeur finale est exacte
        const finalFormattedValue = this.formatNumber(targetValue, separator, decimalPlaces);
        element.textContent = prefix + finalFormattedValue + suffix;
      }
    };
    
    requestAnimationFrame(animateCounter);
  }

  /**
   * Obtenir le nombre de décimales d'un nombre
   */
  getDecimalPlaces(number) {
    if (Number.isInteger(number)) return 0;
    const str = number.toString();
    if (str.indexOf('.') !== -1 && str.indexOf('e-') === -1) {
      return str.split('.')[1].length;
    } else if (str.indexOf('e-') !== -1) {
      const parts = str.split('e-');
      return parseInt(parts[1], 10) + (parts[0].split('.')[1] ? parts[0].split('.')[1].length : 0);
    }
    return 0;
  }

  /**
   * Arrondir un nombre à un nombre spécifique de décimales
   */
  roundToDecimalPlaces(number, decimalPlaces) {
    if (decimalPlaces === 0) return Math.round(number);
    const factor = Math.pow(10, decimalPlaces);
    return Math.round(number * factor) / factor;
  }

  /**
   * Formater un nombre avec séparateur et décimales
   */
  formatNumber(number, separator = '', decimalPlaces = 0) {
    // Arrondir d'abord si nécessaire
    const roundedNumber = this.roundToDecimalPlaces(number, decimalPlaces);
    
    // Formater avec les décimales
    const formattedNumber = roundedNumber.toFixed(decimalPlaces);
    
    // Appliquer le séparateur de milliers si nécessaire
    if (separator === '') return formattedNumber;
    
    const parts = formattedNumber.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return parts.join('.');
  }

  /**
   * Méthode publique pour déclencher une animation manuellement
   */
  triggerAnimation(element, type, options = {}) {
    if (type.startsWith('entry:')) {
      const animationType = type.replace('entry:', '');
      this.triggerEntryAnimation(element, animationType);
    } else if (type.startsWith('parallax:')) {
      const direction = type.replace('parallax:', '');
      const speed = options.speed || 0.5;
      this.triggerParallaxAnimation(element, speed, direction);
    } else if (type.startsWith('counter:')) {
      const targetValue = options.targetValue || parseFloat(element.dataset.counterTarget) || 0;
      const duration = options.duration || parseInt(element.dataset.counterDuration) || 2000;
      const prefix = options.prefix || element.dataset.counterPrefix || '';
      const suffix = options.suffix || element.dataset.counterSuffix || '';
      const separator = options.separator || element.dataset.counterSeparator || '';
      this.triggerCounterAnimation(element, targetValue, duration, prefix, suffix, separator);
    }
  }

  /**
   * Méthode publique pour ajouter un nouvel élément à observer
   */
  observeElement(element, type) {
    const observer = this.observers.get(type);
    if (observer) {
      if (type === 'entry') {
        const animationType = element.dataset.animate || 'fadeInUp';
        this.applyEntryAnimationBase(element, animationType);
      } else if (type === 'parallax') {
        this.registerParallaxItem(element);
        this.handleParallaxScroll();
      }

      observer.observe(element);
    }
  }

  /**
   * Nettoyer les observers
   */
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.animations.forEach(animation => animation.destroy());
    this.animations.clear();
    this.observers.clear();

    if (this.parallaxListenersAttached) {
      window.removeEventListener('scroll', this.handleParallaxScroll);
      window.removeEventListener('resize', this.handleParallaxScroll);
      this.parallaxListenersAttached = false;
    }

    if (this.parallaxRafId) {
      cancelAnimationFrame(this.parallaxRafId);
      this.parallaxRafId = null;
    }

    this.parallaxItems.clear();
    this.parallaxTicking = false;
  }
}

// Initialiser le gestionnaire d'animations
const animationManager = new AnimationManager();

// Exposer globalement pour utilisation manuelle
window.AnimationManager = animationManager;

// Export pour modules (si supporté)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AnimationManager;
}
